import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Checkbox,
  Stack,
  useColorModeValue,
  Alert,
  AlertIcon,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";

export default function SuggestionModal({
  isOpen,
  onClose,
  suggestions,
  onApprove,
  isLoading,
}) {
  const [selectedKeywords, setSelectedKeywords] = useState({});
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgColor = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  // Initialize selectedKeywords when modal opens
  useEffect(() => {
    if (isOpen && suggestions && suggestions.keyword_negatives) {
      const initialized = {};
      for (const keyword_id in suggestions.keyword_negatives) {
        initialized[keyword_id] = [];
      }
      setSelectedKeywords(initialized);
      setActiveTabIndex(0); // reset to first tab
    }
  }, [isOpen, suggestions]);

  const handleToggleKeyword = (keyword_id, keyword) => {
    setSelectedKeywords((prev) => {
      const updated = { ...prev };
      if (!updated[keyword_id]) updated[keyword_id] = [];

      if (updated[keyword_id].includes(keyword)) {
        updated[keyword_id] = updated[keyword_id].filter((k) => k !== keyword);
      } else {
        updated[keyword_id].push(keyword);
      }

      return updated;
    });
  };

  const handleSelectAll = () => {
    if (!suggestions || !suggestions.keyword_negatives) return;

    const currentKeywordId = Object.keys(suggestions.keyword_negatives)[activeTabIndex];
    const keywordData = suggestions.keyword_negatives[currentKeywordId];

    const totalKeywords =
      (keywordData.brand_negative?.length || 0) +
      (keywordData.compound_negative?.length || 0);
    const currentSelected = selectedKeywords[currentKeywordId]?.length || 0;

    setSelectedKeywords((prev) => {
      const updated = { ...prev };
      if (currentSelected === totalKeywords) {
        // Deselect all for this tab
        updated[currentKeywordId] = [];
      } else {
        // Select all for this tab
        updated[currentKeywordId] = [
          ...(keywordData.brand_negative || []),
          ...(keywordData.compound_negative || []),
        ];
      }
      return updated;
    });
  };

  const handleApprove = () => {
    const hasSelection = Object.values(selectedKeywords).some(
      (arr) => arr.length > 0
    );

    if (!hasSelection) {
      alert("Please select at least one keyword to approve");
      return;
    }

    onApprove(selectedKeywords);
  };

  if (!suggestions?.keyword_negatives) return null;

  const keywordIds = Object.keys(suggestions.keyword_negatives);

  // Determine if all keywords in current tab are selected
  const allSelected = (() => {
    if (!suggestions || !suggestions.keyword_negatives) return false;
    const currentKeywordId = keywordIds[activeTabIndex];
    const keywordData = suggestions.keyword_negatives[currentKeywordId];
    const totalKeywords =
      (keywordData.brand_negative?.length || 0) +
      (keywordData.compound_negative?.length || 0);
    const currentSelected = selectedKeywords[currentKeywordId]?.length || 0;
    return currentSelected === totalKeywords;
  })();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={textColor}>
          Review Negative Keywords by Keyword
        </ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        <Divider />
        <ModalBody>
          {keywordIds.length === 0 ? (
            <Alert status="warning" borderRadius="8px">
              <AlertIcon />
              No suggestions available for these products.
            </Alert>
          ) : (
            <Tabs
              variant="soft-rounded"
              colorScheme="blue"
              index={activeTabIndex}
              onChange={(index) => setActiveTabIndex(index)}
            >
              <TabList mb="1em">
                {keywordIds.map((keyword_id) => (
                  <Tab key={keyword_id}>Keyword {keyword_id}</Tab>
                ))}
              </TabList>

              <TabPanels>
                {keywordIds.map((keyword_id) => {
                  const keywordData = suggestions.keyword_negatives[keyword_id];
                  const brandNegatives = keywordData.brand_negative || [];
                  const compoundNegatives = keywordData.compound_negative || [];
                  const selectedCount = selectedKeywords[keyword_id]?.length || 0;

                  return (
                    <TabPanel key={keyword_id}>
                      <VStack align="stretch" spacing="20px">
                        <Alert status="info" borderRadius="8px">
                          <AlertIcon />
                          Select the negative keywords you want to add for this
                          keyword. Selected: {selectedCount} /{" "}
                          {brandNegatives.length + compoundNegatives.length}
                        </Alert>

                        {/* Brand Negative Keywords */}
                        {brandNegatives.length > 0 && (
                          <Box>
                            <HStack mb="12px">
                              <Text fontSize="sm" fontWeight="600" color={textColor}>
                                Brand Level Filters
                              </Text>
                              <Badge colorScheme="blue">{brandNegatives.length}</Badge>
                            </HStack>
                            <Stack
                              pl="4px"
                              spacing="8px"
                              p="12px"
                              bg={bgColor}
                              borderRadius="8px"
                              border="1px solid"
                              borderColor={borderColor}
                            >
                              {brandNegatives.map((keyword, index) => (
                                <HStack key={index} spacing="10px">
                                  <Checkbox
                                    isChecked={
                                      selectedKeywords[keyword_id]?.includes(keyword) || false
                                    }
                                    onChange={() =>
                                      handleToggleKeyword(keyword_id, keyword)
                                    }
                                  />
                                  <Text fontSize="sm" color={textColor}>
                                    {keyword}
                                  </Text>
                                </HStack>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Compound Negative Keywords */}
                        {compoundNegatives.length > 0 && (
                          <Box>
                            <HStack mb="12px">
                              <Text fontSize="sm" fontWeight="600" color={textColor}>
                                Compound Filters
                              </Text>
                              <Badge colorScheme="purple">{compoundNegatives.length}</Badge>
                            </HStack>
                            <Stack
                              pl="4px"
                              spacing="8px"
                              p="12px"
                              bg={bgColor}
                              borderRadius="8px"
                              border="1px solid"
                              borderColor={borderColor}
                            >
                              {compoundNegatives.map((keyword, index) => (
                                <HStack key={index} spacing="10px">
                                  <Checkbox
                                    isChecked={
                                      selectedKeywords[keyword_id]?.includes(keyword) || false
                                    }
                                    onChange={() =>
                                      handleToggleKeyword(keyword_id, keyword)
                                    }
                                  />
                                  <Text fontSize="sm" color={textColor}>
                                    {keyword}
                                  </Text>
                                </HStack>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </VStack>
                    </TabPanel>
                  );
                })}
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>

        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={handleSelectAll} isDisabled={isLoading}>
            {allSelected ? "Deselect All" : "Select All"}
          </Button>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button colorScheme="green" onClick={handleApprove} isLoading={isLoading}>
            Approve & Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
